import {
  Route,
  Routes,
  useNavigate,
  useLocation,
  Navigate,
} from "react-router-dom";
import React, { useState, useEffect, lazy, Suspense } from "react";
import "./App.css";
import "./Responsive.css";
import "react-toastify/dist/ReactToastify.css";
import { useAuth } from "./Contexts/AuthContext";
import BASE_URLS from "./baseUrl";

// Eager: rendered on every page, so splitting them would only add round trips.
import AppBar from "./Components/AppBar";
import Footer from "./Components/Footer";
import Loader from "./Loader";
import Home from "./Components/Home/Home";

/*
 * Every other route is code-split with React.lazy, so its JavaScript is only
 * downloaded when someone actually navigates there. Previously all 30 routes
 * were bundled into one ~1.2MB main.js, meaning a visitor to the homepage
 * also downloaded the entire checkout flow, PDF generation and user account
 * screens before anything rendered.
 *
 * Components exported as named rather than default need the .then() shim,
 * because React.lazy resolves the module's `default` export.
 */
const named = (importFn, exportName) =>
  lazy(() => importFn().then((m) => ({ default: m[exportName] })));

const AllDestinations = lazy(() => import("./Components/AllDestinations"));
const ThingstoDo = lazy(() => import("./Components/ThingsToDo/ThingToDo"));
const CheckOut = lazy(() => import("./Components/CheckOut/CheckOut"));
const PrivacyPolicy = lazy(() => import("./Components/PrivacyPolicy"));
const CookiesPolicy = lazy(() => import("./Components/CookiesPolicy"));
const TermsOfUse = lazy(() => import("./Components/TermsOfUse"));
const SettingsTab = lazy(() => import("./Components/UserProfile/SettingsTab"));
const ProfileTab = lazy(() => import("./Components/UserProfile/ProfileTab"));
const BookingTab = lazy(() => import("./Components/UserProfile/BookingTab"));
const ReviewTab = lazy(() => import("./Components/UserProfile/ReviewTab"));
const ResetPassword = lazy(() => import("./Components/resetPassword"));
const Blog = lazy(() => import("./Components/Blog/Blog"));
const BlogDetails = lazy(() => import("./Components/BlogDetails/BlogDetails"));

const Activity = named(() => import("./Components/Activity/Activity"), "Activity");
const ActivityList = named(() => import("./Components/ActivityList/ActivityList"), "ActivityList");
const About = named(() => import("./Components/About/About"), "About");
const HelpCenter = named(() => import("./Components/HelpCenter/HelpCenter"), "HelpCenter");
const Articles = named(() => import("./Components/HelpCenter/Article"), "Articles");
const NotFound = named(() => import("./Components/NotFound"), "NotFound");
const FaqForm = named(() => import("./Components/HelpCenter/FaqForm"), "FaqForm");
const ContactUs = named(() => import("./Components/Contact/ContactUs"), "ContactUs");
const ForgotPassword = named(() => import("./Components/ForgotPassword"), "ForgotPassword");
const Wishlist = named(() => import("./Components/UserProfile/Wishlist"), "Wishlist");
const BookingConfirmation = named(() => import("./Components/CheckOut/BookingConfirmation"), "BookingConfirmation");
const BookingSuccess = named(() => import("./Components/CheckOut/BookingSuccess"), "BookingSuccess");
const BookingFailure = named(() => import("./Components/CheckOut/BookingFailure"), "BookingFailure");
const PaymentRedirect = named(() => import("./Components/CheckOut/PaymentRedirect"), "PaymentRedirect");
const OrderDetails = named(() => import("./Components/UserProfile/OrderDetails"), "OrderDetails");

function App({ user }) {
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate(); // Add this line
  const location = useLocation();
  const webUrl = BASE_URLS.webUrl;

  const { isLoggedIn, isLoggedInWithGmail, handleGoogleLogout } = useAuth();

  useEffect(() => {
    setTimeout(() => {
      setLoading(false);
      window.scrollTo(0, 0);
    }, 100);
  }, [location.pathname]);

  return (
    <>
      <AppBar
        user={user}
        isLoggedInWithGmail={isLoggedInWithGmail}
        isLoggedIn={isLoggedIn}
        navigate={navigate}
        handleGoogleLogout={handleGoogleLogout}
      />
      {loading ? (
        <Loader />
      ) : (
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path={webUrl} element={<Home />} />
          <Route path={`${webUrl}destinations`} element={<AllDestinations />} />
          <Route
            path={`${webUrl}things-to-do/:city`}
            element={<ThingstoDo />}
          />
          <Route path={`${webUrl}activity/:id`} element={<Activity />} />
          <Route path={`${webUrl}activity-list`} element={<ActivityList />} />
          <Route
            path={`${webUrl}checkout`}
            isLoggedIn={isLoggedIn}
            element={<CheckOut />}
          />
          <Route path={`${webUrl}privacy-policy`} element={<PrivacyPolicy />} />
          <Route path={`${webUrl}cookie-policy`} element={<CookiesPolicy />} />
          <Route path={`${webUrl}terms-of-use`} element={<TermsOfUse />} />
          <Route path={`${webUrl}about-us`} element={<About />} />
          <Route path={`${webUrl}contact-us`} element={<ContactUs />} />
          <Route path={`${webUrl}help-center`} element={<HelpCenter />} />
          <Route path={`${webUrl}articles/:cat_id`} element={<Articles />} />
          <Route path={`${webUrl}faq-form`} element={<FaqForm />} />
          <Route path={`${webUrl}forgot-password`}  element={
              isLoggedIn ? (
                <Navigate to={webUrl} />
              ) : (
                <ForgotPassword />
              )
            }
          />
          <Route path={`${webUrl}reset-password/:token`}  element={
              isLoggedIn ? (
                <Navigate to={webUrl} />
              ) : (
                <ResetPassword />
              )
            }
          />
          <Route
            path={`${webUrl}order-summary`}
            element={
              isLoggedIn ? <BookingConfirmation /> : <Navigate to={webUrl} />
            }
          />
          <Route
            path={`${webUrl}booking-success`}
            element={<BookingSuccess />}
          />
          <Route
            path={`${webUrl}booking-failure`}
            element={<BookingFailure />}
          />
          <Route path={`${webUrl}redirect_url`} element={<PaymentRedirect />} />

          <Route
            path={`${webUrl}wishlist`}
            element={isLoggedIn ? <Wishlist /> : <Navigate to={webUrl} />}
          />
          <Route
            path={`${webUrl}account/bookings`}
            element={isLoggedIn ? <BookingTab /> : <Navigate to={webUrl} />}
          />
          <Route
            path={`${webUrl}account/reviews`}
            element={isLoggedIn ? <ReviewTab /> : <Navigate to={webUrl} />}
          />
          <Route
            path={`${webUrl}account/profile`}
            element={isLoggedIn ? <ProfileTab /> : <Navigate to={webUrl} />}
          />
          <Route
            path={`${webUrl}account/settings`}
            element={isLoggedIn ? <SettingsTab /> : <Navigate to={webUrl} />}
          />
          <Route
            path={`${webUrl}account/order-details/:orderId`}
            element={isLoggedIn ? <OrderDetails /> : <Navigate to={webUrl} />}
          />  
          <Route
            path={`${webUrl}blog`}
            element={<Blog />}
          />
          <Route
            path={`${webUrl}blog/:blogSlug`}
            element={<BlogDetails />}
          />

          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      )}
      <Footer />
    </>
  );
}

export default App;
